import React, { useState, useRef } from 'react';
import { 
  PlusIcon, 
  DocumentIcon, 
  PhotoIcon, 
  VideoCameraIcon,
  MusicalNoteIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const TaskFileUpload = ({ 
  taskId, 
  files = [], 
  onFileUploaded, 
  onFileDeleted, 
  disabled = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(type)) {
      return <PhotoIcon className="h-8 w-8 text-blue-500" />;
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(type)) {
      return <VideoCameraIcon className="h-8 w-8 text-purple-500" />;
    }
    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(type)) {
      return <MusicalNoteIcon className="h-8 w-8 text-green-500" />;
    }
    return <DocumentIcon className="h-8 w-8 text-gray-500" />;
  };

  const getFileTypeCategory = (fileType) => {
    const type = fileType?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(type)) {
      return 'image';
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(type)) {
      return 'video';
    }
    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(type)) {
      return 'audio';
    }
    return 'document';
  };

  const handleFileSelect = (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    Array.from(selectedFiles).forEach(async (file) => {
      try {
        // Client-side file size validation (2MB = 2 * 1024 * 1024 bytes)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
          alert(`File "${file.name}" is too large. Maximum size is 2MB.`);
          return;
        }

        setUploading(true);
        const fileData = {
          file: file,
          description: ''
        };
        
        await onFileUploaded(fileData);
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Failed to upload file: ' + file.name);
      } finally {
        setUploading(false);
      }
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePreview = (file) => {
    const category = getFileTypeCategory(file.file.file_type);
    alert(`File Type: ${category}\nFormat: ${file.file.file_type?.toUpperCase()}`);
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.file.url;
    link.download = file.file.url?.split('/').pop() || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3" style={{ display: 'none' }}>
      {/* Show descriptions for files that have them */}
      {files.some(file => file.description) && (
        <div>
          <h4 className="text-xs font-medium text-gray-700 mb-2">File Descriptions:</h4>
          {files
            .filter(file => file.description)
            .map((file) => (
              <div key={file.id} className="text-xs text-gray-600 mb-1">
                <span className="font-medium">
                  {file.file.url?.split('/').pop()?.substring(0, 15) || 'Unknown'}:
                </span>{' '}
                {file.description}
              </div>
            ))
          }
        </div>
      )}

      {/* File Grid */}
      <div className="grid grid-cols-4 gap-4">
        {/* Existing Files as Squares */}
        {files.map((file) => (
          <div key={file.id} className="relative group">
            {/* File Square - Always show icon, never show actual image */}
            <div className="w-24 h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative overflow-hidden hover:bg-gray-50 transition-colors">
              <div className="flex flex-col items-center">
                {React.cloneElement(getFileIcon(file.file.file_type), {
                  className: "h-10 w-10 text-gray-600"
                })}
                <span className="text-xs text-gray-500 mt-2 truncate w-full text-center px-1">
                  {file.file.file_type?.toUpperCase()}
                </span>
              </div>
              
              {/* Action buttons overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePreview(file)}
                  className="p-2 text-white hover:text-blue-200 rounded"
                  title="Show file type"
                >
                  <EyeIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDownload(file)}
                  className="p-2 text-white hover:text-green-200 rounded"
                  title="Download"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onFileDeleted(file.id)}
                  className="p-2 text-white hover:text-red-200 rounded"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* File name below square */}
            <p className="text-xs text-gray-600 mt-2 truncate text-center">
              {file.file.url?.split('/').pop()?.substring(0, 15) || 'Unknown'}...
            </p>
          </div>
        ))}

        {/* Upload Square */}
        {!disabled && (
          <div
            className={`
              w-24 h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors relative overflow-hidden
              ${dragOver 
                ? 'border-blue-400' 
                : 'border-gray-300 hover:border-gray-400'
              }
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={openFileDialog}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={disabled}
            />
            
            {/* Main area - top 2/3 */}
            <div className={`
              h-16 flex items-center justify-center
              ${dragOver ? 'bg-blue-50' : uploading ? 'bg-gray-100' : 'hover:bg-gray-50'}
            `}>
              {uploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              ) : (
                <PlusIcon className="h-8 w-8 text-gray-400" />
              )}
            </div>
            
            {/* Bottom third with iavisus-100 color */}
            <div className="h-8 bg-iavisus-100 flex items-center justify-center">
              <span className="text-xs text-gray-600 font-medium">Upload</span>
            </div>
          </div>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        {disabled 
          ? "Save the task first to add file attachments" 
          : "Click the + square or drag files to upload. Max 2MB per file."
        }
      </p>

    </div>
  );
};

export default TaskFileUpload;